import config from 'config'
import store from 'config/store'
import { hasActiveConversationBeenInteractedWith } from 'modules/conversations/selectors'
import { rejoinChannel } from 'modules/session/actions'
import { Channel, Socket } from 'phoenix'
import { isMobileDevice } from 'shared/src/utils/platform'
import { log } from 'utils'
import { resolvePresenceAPIBase, resolveWSAPIBase } from 'utils/api'
import { SOCKET_CHANNELS, SOCKET_CLUSTER } from 'utils/socket'

interface OpenSocketArgs {
  session_token: string
  org_id: number
  socketCluster: string
  ip: string
}

interface JoinChannelArgs {
  channelName: string
  socket: Socket
  events: any
}

class RefetchMessagesState {
  isJoinAfterConnectionLost: boolean

  constructor() {
    this.isJoinAfterConnectionLost = false
  }

  shouldRefetchMessagesOnChannelJoin() {
    return this.isJoinAfterConnectionLost
  }

  onSocketDisconnect() {
    this.isJoinAfterConnectionLost = true
  }

  resetStateOnJoin() {
    this.isJoinAfterConnectionLost = false
  }
}

const refetchMessagesState = new RefetchMessagesState()

export const OpenSocket = ({ session_token, org_id, socketCluster, ip }: OpenSocketArgs) => {
  const protocol = config.WS_PROTOCOL
  const orgPrefix = config.WS_USER_ENV === 'LOCAL' ? `` : `${org_id}-`

  const socketBase = _resolveSocketBaseForType(org_id, orgPrefix, socketCluster)

  const socketUrl = `${protocol}://${socketBase}/ws`
  const socket = new Socket(socketUrl, {
    params: { session_token, remote_ip: ip },
    timeout: 10000,
    reconnectAfterMs: (attempt) => [1000, 2000, 5000, 10000][attempt - 1] || 10000,
    rejoinAfterMs: (tries) => [1000, 2000, 5000][tries - 1] || 10000,
  })

  socket.connect()
  socket.onError(() => {
    if (socketCluster === SOCKET_CLUSTER.CHAT) {
      refetchMessagesState.onSocketDisconnect()
    }
  })
  socket.onClose(() => {
    if (socketCluster === SOCKET_CLUSTER.CHAT) {
      refetchMessagesState.onSocketDisconnect()
    }
  })

  return socket
}

export const JoinChannel = async ({ channelName, socket, events }: JoinChannelArgs) => {
  const ChannelPromise = new Promise<{ channel: Channel }>(async (resolve, reject) => {
    const channel = socket.channel(channelName)

    events.forEach(({ topic, event }) => {
      channel.on(topic, event)
    })

    channel
      .join()
      .receive('ok', () => {
        if (
          hasActiveConversationBeenInteractedWith() &&
          refetchMessagesState.shouldRefetchMessagesOnChannelJoin() &&
          channelName.startsWith(SOCKET_CHANNELS.USER)
        ) {
          log({
            data: [
              `joined user channel ${channelName} after disconnect and user interacted with active conversation`,
              `is_mobile_${isMobileDevice(true)}`
            ],
            internal: true,
          })
          store.dispatch(rejoinChannel({
            channel: SOCKET_CHANNELS.USER,
            shouldRefetchMessagesForActiveConversation: true
          }))
          refetchMessagesState.resetStateOnJoin()
        }

        resolve({ channel })
      })
      .receive('error', (err) => {
        log({ data: [`Error connecting to channel: ${channelName}`] })
        reject(err)
      })
      .receive('timeout', (err) => {
        log({ data: [`Joining the ${channelName} channel timed out. `] })
      })
  })

  return ChannelPromise
}

const _resolveSocketBaseForType = (orgId: number, orgPrefix: string, socketCluster: string) => {
  switch (socketCluster) {
    case SOCKET_CLUSTER.CHAT:
      return `${resolveWSAPIBase(orgId)}`
    case SOCKET_CLUSTER.USER:
      return config.WS_USER_BASE
    case SOCKET_CLUSTER.PRESENCE:
      return `${orgPrefix}${resolvePresenceAPIBase(orgId)}`
    case SOCKET_CLUSTER.VISITOR_PRESENCE:
      return config.WS_VISITOR_PRESENCE_BASE
    default:
      return ''
  }
}
